import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, Loader2 } from "lucide-react";

// Admin-only one-time trigger: send the attempt evidence email for AR-33099.
// Refuses to send twice (same attempt_email_sent_at guard as the script).
export function SendAttemptEmailButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/send-attempt-email/AR-33099");
      setResult(data);
      if (data.status === "sent") toast.success(`Attempt email sent for AR-33099`);
      else toast.info(data.message || "Already sent");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Send failed";
      setResult({ status: "error", message: msg });
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const tone =
    result?.status === "sent" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
    : result?.status === "already_sent" ? "border-amber-200 bg-amber-50 text-amber-800"
    : "border-red-200 bg-red-50 text-red-800";

  return (
    <div data-testid="send-attempt-email" className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-900">Send attempt email — AR-33099</p>
          <p className="text-xs text-slate-500">One-time: emails the client the logged unsuccessful attempt. Refuses to send twice.</p>
        </div>
        <Button data-testid="send-attempt-email-btn" size="sm" disabled={busy} onClick={run}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />} Send now
        </Button>
      </div>
      {result && (
        <div data-testid="send-attempt-email-result" className={`mt-3 rounded-lg border p-3 text-sm ${tone}`}>
          <p className="font-semibold">{result.message}</p>
          {result.status === "sent" && (
            <p className="mt-1 text-xs">
              to {result.to || "—"} · message_id <span className="font-mono">{result.message_id || "—"}</span> · log {result.log_status || "—"}
            </p>
          )}
          {result.status === "already_sent" && result.attempt_email_sent_at && (
            <p className="mt-1 text-xs">sent at {result.attempt_email_sent_at}</p>
          )}
        </div>
      )}
    </div>
  );
}
